import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/lib/supabase";
import { Sentry } from "@/lib/sentry";
import { useProfile } from "@/lib/useProfile";
import { QuickAddSheet } from "@/components/quick-add-sheet";

const BG = "var(--caileve-bg)";
const INK = "var(--caileve-ink)";
const LILAC = "var(--caileve-lilac)";
const COPPER = "var(--caileve-copper)";
const SOFT = "var(--caileve-soft)";
const SERIF = "'Cormorant Garamond', Georgia, serif";

export const Route = createFileRoute("/onboarding")({
  head: () => ({
    meta: [
      { title: "Welcome — Cailève" },
      { name: "description", content: "Welcome to your Cailève." },
    ],
  }),
  component: OnboardingPage,
});

function OnboardingPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [captureStep, setCaptureStep] = useState<0 | 1 | 2>(0);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [loading, user, navigate]);

  const {
    data: profile,
    error: profileError,
    isLoading: profileLoading,
    refetch: refetchProfile,
  } = useProfile();

  const complete = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("profiles")
        .upsert({ id: user?.id, onboarding_complete: true } as never);
      if (error) throw new Error(error.message || "Network error");
    },
  });

  // If already completed, bounce to home
  useEffect(() => {
    if (profile && (profile as { onboarding_complete?: boolean }).onboarding_complete) {
      navigate({ to: "/" });
    }
  }, [profile, navigate]);

  const finish = (to: "/" | "/jewelry" | "/clothing" | "/accessories") => {
    // Optimistically mark onboarding complete in the AppShell's onboardCheck
    // cache BEFORE navigating, so its redirect effect cannot bounce the user
    // back to /onboarding during the navigation window.
    const uid = user?.id;
    if (!uid) return;
    qc.setQueryData(
      ["onboarding_check", uid],
      (old: { done: boolean; empty: boolean } | undefined) =>
        old ? { ...old, done: true } : old,
    );
    // Fire-and-forget the completion write so a slow/failed network call
    // never blocks navigation on mobile. The profile row is also auto-healed
    // elsewhere on resume.
    complete.mutate(undefined, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["onboarding_check", uid] });
        qc.invalidateQueries({ queryKey: ["profile", uid] });
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    });
    if (to === "/jewelry") navigate({ to: "/jewelry", search: { new: 1 } as never });
    else if (to === "/clothing") navigate({ to: "/clothing", search: { new: 1 } as never });
    else if (to === "/accessories") navigate({ to: "/accessories", search: { new: 1 } as never });
    else navigate({ to: "/" });
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BG, color: INK }}>
        <p style={{ fontFamily: SERIF }} className="text-2xl">Cailève</p>
      </div>
    );
  }

  if (profileError && !profileLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ backgroundColor: BG, color: INK }}
      >
        <div className="max-w-md text-center">
          <h1 className="text-3xl" style={{ fontFamily: SERIF, color: LILAC }}>
            Having trouble connecting.
          </h1>
          <p className="mt-4 text-base leading-relaxed" style={{ fontFamily: SERIF }}>
            Please check your connection and try again.
          </p>
          <button
            type="button"
            onClick={() => refetchProfile()}
            className="mt-6 px-6 py-2.5 text-xs uppercase tracking-[0.25em] text-[var(--caileve-bg)]"
            style={{ backgroundColor: LILAC }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG, color: INK }}>
      <section className="max-w-2xl mx-auto px-6 py-16 text-center">
        <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: LILAC }}>
          Your First Three Pieces
        </p>
        <h2 className="text-4xl md:text-5xl mt-4" style={{ fontFamily: SERIF, color: INK }}>
          {captureStep === 0 && "The piece you reach for without thinking."}
          {captureStep === 1 && "Something that feels completely like you."}
          {captureStep === 2 && "A piece you save for the moments that matter."}
        </h2>
        <p className="italic mt-4 text-lg" style={{ fontFamily: SERIF, color: LILAC }}>
          {captureStep === 0 && "Frequency. The piece Cailève will learn to know first."}
          {captureStep === 1 && "Identity. The one that tells Cailève who you are."}
          {captureStep === 2 && "Aspiration. The piece that shows Cailève your occasions."}
        </p>

        <div className="flex justify-center gap-2 mt-10">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: i === captureStep ? 24 : 8,
                height: 4,
                borderRadius: "var(--caileve-radius-pill)",
                backgroundColor: i <= captureStep ? LILAC : SOFT,
                transition: "all 300ms ease",
              }}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="mt-12 px-10 py-4 text-sm uppercase tracking-[0.25em] hover:opacity-90 transition-opacity"
          style={{ backgroundColor: LILAC, color: "var(--caileve-bg)", border: "none", cursor: "pointer" }}
        >
          {captureStep === 0 ? "Capture this piece" : captureStep === 1 ? "Photograph another" : "Complete your three"}
        </button>

        <div className="mt-8 flex flex-col items-center gap-4" style={{ paddingBottom: 80 }}>
          <button
            type="button"
            onClick={() => finish("/")}
            className="text-sm opacity-50 underline underline-offset-4"
            style={{ color: LILAC, background: "none", border: "none", cursor: "pointer" }}
          >
            {captureStep === 0 ? "Skip and go to my collection" : "I'm done for now"}
          </button>
          <button
            type="button"
            onClick={() => {
              supabase.auth.signOut().then(() => navigate({ to: "/login" }));
            }}
            className="text-xs opacity-40 underline underline-offset-4"
            style={{ color: LILAC, background: "none", border: "none", cursor: "pointer", fontFamily: SERIF, fontStyle: "italic" }}
          >
            Returning user? Sign in to your existing account.
          </button>
        </div>
      </section>
      <QuickAddSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        userId={user.id}
        defaultType={captureStep === 2 ? "clothing" : "jewelry"}
        onDone={() => {
          setAddOpen(false);
          if (captureStep < 2) {
            setCaptureStep((s) => (s + 1) as 0 | 1 | 2);
          } else {
            finish("/");
          }
        }}
      />
    </div>
  );
}
